/**
 * Replaces [total] shortcodes in text with formatted price values
 *
 * @param props - Function parameters
 * @param props.context - Shortcode context containing text and locale
 * @param props.total - Base price value to display
 * @param props.totalDiscount - Optional discounted price value
 *
 * @returns Updated context with replaced shortcodes
 *
 * Behavior:
 * - Without discount: Replaces [total] with formatted base price
 * - With discount: Replaces [total] with strikethrough base price + discounted price
 * - Formats prices according to locale
 *
 * @example
 * ```ts
 * // Without discount
 * applyTotal({
 *   context: { text: "Price: [total]", locale: "en" },
 *   total: 100
 * });
 * // Result: "Price: $100"
 *
 * // With discount
 * applyTotal({
 *   context: { text: "Price: [total]", locale: "en" },
 *   total: 100,
 *   totalDiscount: 80
 * });
 * // Result: "Price: <span class="line-through text-[#96A3B3]">$100</span> $80"
 * ```
 */
import { ShortcodeContext } from '..';
import { formatPrice } from '../../formatPrice';

export const applyTotal = ({
  context,
  total,
  totalDiscount,
}: {
  context: ShortcodeContext;
  total: number;
  totalDiscount?: number | null;
}) => {
  if (!totalDiscount) {
    context.text = context.text.replace(
      /\[total\]/g,
      formatPrice(total, context.locale),
    );
  }
  if (total && totalDiscount) {
    context.text = context.text.replace(
      /\[total\]/g,
      `<span class="line-through text-[#96A3B3]">${formatPrice(total, context.locale)}</span> ${formatPrice(totalDiscount, context.locale)}`,
    );
  }
  return context;
};
